/**
 * DiagramRenderer — renders a `diagram` artifact (mermaid / graphviz / svg).
 *
 * Strategy:
 *   1. If `payload.rendered_url` is set, the backend already produced an
 *      image — render it as `<img>` regardless of `format`. This is the fast
 *      path and avoids re-running a renderer in the browser.
 *   2. Otherwise dispatch on `payload.format`:
 *      - "mermaid"  -> reuse the canonical `MermaidBlock` (chat surface)
 *      - "graphviz" -> show source in `<pre>` with a "rendering not
 *        implemented" hint (TODO: bring in viz.js or a backend renderer)
 *      - "svg"      -> sanitize via DOMPurify (svg profile) and inject as HTML
 *
 * Default actions when `artifact.actions` is empty:
 *   - copy     (the source code; `id="copy"`)
 *   - download (svg or source; `id="download"`)
 *
 * Spec: .workspace-spec/WORKSPACE_CONTRACT.md
 */
import { useMemo } from "react"

import type { Artifact, ArtifactAction, DiagramPayload } from "@/api/models"

import { sanitizeInlineSvg } from "@/features/artifacts/renderers/sanitize-inline-svg"
import { MermaidBlock } from "@/features/chat/components/mermaid-block"

import { ArtifactFrame } from "./ArtifactFrame"

const DEFAULT_DIAGRAM_ACTIONS: ArtifactAction[] = [
  { id: "copy", label: "Copy", icon: "copy" },
  { id: "download", label: "Download", icon: "download" },
]

export interface DiagramRendererProps {
  artifact: Artifact & { payload: DiagramPayload }
  versionChain?: Artifact[]
  onAction?: (actionId: string, artifact: Artifact) => void
  onSelectVersion?: (artifactId: string) => void
  onViewTrace?: (missionId: string) => void
  onShowHistory?: () => void
}

/**
 * Renders an SVG string. The input MUST already be sanitized before reaching
 * this component.
 */
function SanitizedSvg({ svg }: { svg: string }) {
  const dangerousProps = { __html: svg }
  return (
    <div
      className="flex justify-center [&_svg]:h-auto [&_svg]:max-w-full"
      dangerouslySetInnerHTML={dangerousProps}
      data-testid="diagram-renderer-svg"
    />
  )
}

function DiagramBody({ payload }: { payload: DiagramPayload }) {
  // Sanitize SVG up front so the call sits adjacent to the consumer; useMemo
  // keeps the cost stable across re-renders.
  const sanitizedSvg = useMemo(() => {
    if (payload.format !== "svg" || payload.rendered_url) return null
    return sanitizeInlineSvg(payload.source)
  }, [payload.format, payload.rendered_url, payload.source])

  // Fast path: server-rendered preview — render whatever URL the backend gave us.
  if (payload.rendered_url) {
    return (
      <img
        alt="Diagram"
        className="h-auto max-w-full rounded-sm"
        data-testid="diagram-renderer-rendered-img"
        src={payload.rendered_url}
      />
    )
  }

  if (payload.format === "mermaid") {
    return (
      <div data-testid="diagram-renderer-mermaid">
        <MermaidBlock code={payload.source} />
      </div>
    )
  }

  if (payload.format === "svg" && sanitizedSvg != null) {
    return <SanitizedSvg svg={sanitizedSvg} />
  }

  // graphviz — TODO: render via viz.js or pre-render server-side.
  return (
    <div data-testid="diagram-renderer-graphviz">
      <p className="text-muted-foreground mb-2 text-xs italic">
        Graphviz rendering not implemented — showing source.
      </p>
      <pre
        className="bg-muted/40 overflow-x-auto rounded-sm border p-3 text-xs"
        data-testid="diagram-renderer-source"
      >
        <code>{payload.source}</code>
      </pre>
    </div>
  )
}

export function DiagramRenderer({
  artifact,
  versionChain,
  onAction,
  onSelectVersion,
  onViewTrace,
  onShowHistory,
}: DiagramRendererProps) {
  const framedArtifact = useMemo<Artifact>(() => {
    const hasActions = (artifact.actions ?? []).length > 0
    const actions = hasActions ? artifact.actions : DEFAULT_DIAGRAM_ACTIONS
    return { ...artifact, actions }
  }, [artifact])

  return (
    <ArtifactFrame
      artifact={framedArtifact}
      versionChain={versionChain}
      onAction={onAction}
      onSelectVersion={onSelectVersion}
      onShowHistory={onShowHistory}
      onViewTrace={onViewTrace}
    >
      <div data-testid="diagram-renderer-body">
        <DiagramBody payload={artifact.payload} />
      </div>
    </ArtifactFrame>
  )
}
